import Parallax from "parallax-js";

class MouseParallax {
  constructor(selector = ".js-parallax", options = {}, root = document) {
    this.selector = selector;
    this.root = root;

    this.options = {
      relativeInput: true,

      /**
       * カーソルがscene上にある時だけ動かす
       */
      hoverOnly: true,

      /**
       * scene外のカーソル位置で極端に動くのを防ぐ
       */
      clipRelativeInput: true,

      // カーソルと同じ方向に動かす
      invertX: false,
      invertY: false,

      scalarX: 8,
      scalarY: 8,
      frictionX: 0.08,
      frictionY: 0.08,

      ...options,
    };

    this.breakpoint = options.breakpoint || 769;
    this.resizeDelay = options.resizeDelay || 200;

    this.instances = [];
    this.isActive = false;
    this.isDestroyed = false;
    this.resizeTimer = null;

    this.mediaQuery = window.matchMedia(`(min-width: ${this.breakpoint}px)`);
    this.pointerQuery = window.matchMedia("(hover: hover) and (pointer: fine)");

    this.handleMediaChange = this.handleMediaChange.bind(this);
    this.handlePointerChange = this.handlePointerChange.bind(this);
    this.handleResize = this.handleResize.bind(this);

    this.init();
  }

  init() {
    this.update();
    this.bindEvents();
  }

  bindEvents() {
    this.addMediaQueryEvent(this.mediaQuery, this.handleMediaChange);
    this.addMediaQueryEvent(this.pointerQuery, this.handlePointerChange);

    window.addEventListener("resize", this.handleResize, false);
  }

  handleMediaChange() {
    this.update();
  }

  handlePointerChange() {
    this.update();
  }

  handleResize() {
    if (this.isDestroyed) return;

    window.clearTimeout(this.resizeTimer);

    this.resizeTimer = window.setTimeout(() => {
      this.update();
    }, this.resizeDelay);
  }

  update() {
    if (this.isDestroyed) return;

    if (this.canRun()) {
      this.start();
      return;
    }

    this.destroy();
  }

  canRun() {
    return (
      this.mediaQuery.matches &&
      this.pointerQuery.matches &&
      !this.isMobileDevice()
    );
  }

  isMobileDevice() {
    const ua = navigator.userAgent || navigator.vendor || window.opera;

    const isMobileUa =
      /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

    const isIPadOS =
      navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;

    return isMobileUa || isIPadOS;
  }

  start() {
    if (this.isDestroyed) return;
    if (this.isActive) return;

    const scenes = this.getScenes();

    if (!scenes.length) return;

    scenes.forEach((scene) => {
      const instance = new Parallax(scene, {
        ...this.options,

        /**
         * 各scene自身を入力範囲にする
         */
        inputElement: scene,
      });

      this.instances.push(instance);
    });

    this.isActive = true;
  }

  getScenes() {
    if (this.root && typeof this.root.querySelectorAll === "function") {
      return Array.from(this.root.querySelectorAll(this.selector));
    }

    return Array.from(document.querySelectorAll(this.selector));
  }

  destroy() {
    if (!this.instances.length) {
      this.isActive = false;
      return;
    }

    this.instances.forEach((instance) => {
      if (instance && typeof instance.destroy === "function") {
        instance.destroy();
      }
    });

    this.instances = [];
    this.isActive = false;
  }

  destroyAll() {
    this.isDestroyed = true;

    window.clearTimeout(this.resizeTimer);
    this.resizeTimer = null;

    this.destroy();

    this.removeMediaQueryEvent(this.mediaQuery, this.handleMediaChange);
    this.removeMediaQueryEvent(this.pointerQuery, this.handlePointerChange);

    window.removeEventListener("resize", this.handleResize, false);
  }

  addMediaQueryEvent(mediaQuery, handler) {
    if (!mediaQuery) return;

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handler, false);
      return;
    }

    if (typeof mediaQuery.addListener === "function") {
      mediaQuery.addListener(handler);
    }
  }

  removeMediaQueryEvent(mediaQuery, handler) {
    if (!mediaQuery) return;

    if (typeof mediaQuery.removeEventListener === "function") {
      mediaQuery.removeEventListener("change", handler, false);
      return;
    }

    if (typeof mediaQuery.removeListener === "function") {
      mediaQuery.removeListener(handler);
    }
  }
}

export default MouseParallax;
